import { Button, Dropdown, Menu, Drawer } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Input, SwitchButton } from "components/base";
import { useHistory, useLocation } from "react-router-dom";
import { setModalName, setUserSearch } from "store/actions";
import {
  actionMenu,
  archiveIcon,
  exportIcon,
  plusIcon,
  searchIcon,
} from "svgs";
import { ROUTES } from "utils/constants";
import { AppState } from "store";
import {
  SearchOutlined,
  PlusOutlined,
  MenuOutlined,
  CloseOutlined,
  ExportOutlined,
  DownloadOutlined,
  UploadOutlined,
} from "@ant-design/icons";

import "styles/components/tab-headers/user-header.scss";

export default function UsersHeader() {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(
    window.innerWidth >= 768 && window.innerWidth < 992
  );
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const selectedView = location.pathname.split("/")[2];
  const { showbulkActionsUser } = useSelector<AppState, AppState["users"]>(
    (state) => state.users
  );
  const {
    common: { userType = 1 },
  } = useSelector<AppState, AppState>((state) => state);

  useEffect(() => {
    dispatch(setUserSearch(""));
  }, [dispatch]);

  // Detect screen size
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const mobile = width < 768;
      const tablet = width >= 768 && width < 992;

      setIsMobile(mobile);
      setIsTablet(tablet);

      // Reset mobile states when switching to desktop
      if (!mobile) {
        setShowMobileSearch(false);
        setMobileMenuOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const onAddUsers = useCallback(() => {
    dispatch(setModalName("add_user"));
    setMobileMenuOpen(false);
  }, [dispatch]);

  const onSwitchView = useCallback(
    (event) => {
      const { value } = event.target;
      history.push(`${ROUTES.users}/${value}`);
      setMobileMenuOpen(false);
    },
    [history]
  );

  const onSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(setUserSearch(event.target.value));
    },
    [dispatch]
  );

  const handleCloseMobileSearch = useCallback(() => {
    setShowMobileSearch(false);
    dispatch(setUserSearch(""));
  }, [dispatch]);

  const menu = (
    <Menu className="users-drop-menu">
      <Menu.Item key="0" disabled>
        <UploadOutlined /> <span>Import</span>
      </Menu.Item>
      <Menu.Item key="1" disabled>
        <DownloadOutlined /> <span>Export</span>
      </Menu.Item>
    </Menu>
  );

  // Mobile menu content
  const mobileMenuContent = (
    <div className="mobile-menu-content">
      {/* Actions */}
      <div className="mobile-menu-section">
        <h4 className="section-title">Actions</h4>
        <div className="mobile-action-buttons">
          <Button type="default" disabled block icon={<UploadOutlined />}>
            Import
          </Button>
          <Button type="default" disabled block icon={<DownloadOutlined />}>
            Export
          </Button>
          {userType != 6 && userType != 3 && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={onAddUsers}
              block
            >
              Add User
            </Button>
          )}
        </div>
      </div>

      {/* Bulk Actions */}
      {showbulkActionsUser && (
        <div className="mobile-menu-section">
          <h4 className="section-title">Bulk Actions</h4>
          <div className="mobile-action-buttons">
            <Button disabled icon={<ExportOutlined />} block>
              Export Selected
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="users-header common-header">
      {/* Desktop View */}
      <div className="header-desktop">
        {/* Search Input */}
        <div className="search-wrapper">
          <Input
            customClasses="--standard"
            placeholder="Search Users..."
            prefix={searchIcon}
            onChange={onSearch}
          />
        </div>

        {/* Bulk Actions */}
        {showbulkActionsUser && (
          <article className="bulk-actions">
            <Button disabled icon={exportIcon}>
              <span className="btn-text">Export</span>
            </Button>
          </article>
        )}

        {/* Actions Container */}
        <div className="actions-container">
          {/* Dropdown Menu */}
          {/* <Dropdown overlay={menu} trigger={["click"]} className="users-menu">
            <a
              className="ant-dropdown-link"
              onClick={(e) => e.preventDefault()}
            >
              {actionMenu}
            </a>
          </Dropdown> */}

          {/* Add User Button */}
          {userType != 6 && userType != 3 && (
            <Button
              type="primary"
              icon={<span className="custom-icon">{plusIcon}</span>}
              onClick={onAddUsers}
            >
              <span className="btn-text">Add User</span>
            </Button>
          )}
        </div>
      </div>

      {/* Mobile View */}
      <div className="header-mobile">
        {/* Top Row */}
        <div className="mobile-top-row">
          {/* Search Toggle or Expanded Search */}
          {showMobileSearch ? (
            <div className="mobile-search-expanded">
              <Input
                customClasses="--standard mobile-search-input"
                placeholder="Search Users..."
                prefix={searchIcon}
                onChange={onSearch}
                autoFocus
              />
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={handleCloseMobileSearch}
                className="search-close-btn"
              />
            
            </div>
         
          ) : (
            <>
              {/* Search Button */}
              <Button
                type="text"
                icon={<SearchOutlined />}
                onClick={() => setShowMobileSearch(true)}
                className="mobile-search-btn"
              />

              {/* Actions Wrapper - Right Side */}
              <div className="mobile-actions-wrapper">
                {/* Add User Button */}
                {userType != 6 && userType != 3 && (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={onAddUsers}
                    className="mobile-add-btn"
                  >
                    <span className="mobile-add-text">Add</span>
                  </Button>
                )}

                {/* Menu Button */}
                {/* <Button
                  type="text"
                  icon={<MenuOutlined />}
                  onClick={() => setMobileMenuOpen(true)}
                  className="mobile-menu-btn"
                /> */}
              </div>
            </>
          )}
        </div>

        {/* Bulk Actions Bar */}
        {/* {showbulkActionsUser && !showMobileSearch && (
          <div className="mobile-bulk-actions">
            <Button size="small" disabled icon={<ExportOutlined />}>
              Export
            </Button>
          </div>
        )} */}
         <h1 style={{ flex: 1,paddingTop: "6px", alignItems: "left" }}>Users</h1>
      </div>

      {/* Mobile Drawer Menu */}
      {/* <Drawer
        title="Menu"
        placement="right"
        onClose={() => setMobileMenuOpen(false)}
        visible={mobileMenuOpen}
        className="users-mobile-drawer"
        width={280}
      >
        {mobileMenuContent}
      </Drawer> */}
    </div>
  );
}